import { fetchResource } from '../utils'
import { logManager, API_LOG_TAG } from '../../logs'
// Other plugins.
import { getPlatform, getRequestInfo } from '../../auth/interface/selectors'
import { merge } from 'lodash/fp'

const log = logManager.getLogger('REQUEST')

/**
 * Request API.
 * @method api
 * @param  {Function} $0
 * @param  {Function} $0.dispatch - The redux store's dispatch function.
 * @param  {Function} $0.getState - The redux store's getState function.
 * @return {Object} api - The request API object.
 */
export default function api ({ dispatch, getState }) {
  return {
    request: {
      /**
       * The 'request' namespace (within the 'api' type) is used to make network requests to the server.
       *
       * @public
       * @namespace request
       */

      /**
       * Send a request to the underlying REST service with the appropriate configuration and authentication.
       * This is a wrapper on top of the browser's [fetch API](https://developer.mozilla.org/en-US/docs/Web/API/fetch)
       * and behaves very similarly but using SDK configuration for the base URL and authentication as well
       * as SDK logging.
       *
       * @public
       * @memberof request
       * @method fetch
       * @param {string} resource The full path of the resource to fetch from the underlying service. This should include any REST version
       *                          or user information. This path will be appended to the base URL according to SDK configuration.
       * @param {RequestInit} init An object containing any custom settings that you want to apply to the request. See [fetch API](https://developer.mozilla.org/en-US/docs/Web/API/fetch)
       *                           for a full description and defaults.
       * @return {Promise<Response>} A promise for a [Response](https://developer.mozilla.org/en-US/docs/Web/API/Response) object.
       * @example
       * // Send a REST request to the server
       * // Create a request options object following [fetch API](https://developer.mozilla.org/en-US/docs/Web/API/fetch)
       * const requestOptions = {
       *   method: 'POST',
       *   body: JSON.stringify({
       *     test: 123
       *   })
       * }
       *
       * // Note that you will need to subscribe for the `custom` service in order to
       * // receive notifications from the `externalnotification` service.
       * const response = await client.request.fetch('/rest/version/1/user/xyz@test.com/externalnotification', requestOptions)
       */
      async fetch (resource, init = {}) {
        log.debug(API_LOG_TAG + 'fetch: ', resource)
        const state = getState()
        const platform = getPlatform(state)
        const requestInfo = getRequestInfo(state, platform)
        const options = platform === 'cpaas' ? requestInfo.options : requestInfo.requestOptions
        requestInfo.requestOptions = merge(init, options)
        requestInfo.platform = platform
        // Do request
        return await fetchResource(resource, requestInfo)
      }
    }
  }
}
